import { useRef, useState } from 'react'
import { acceptedFileTypes } from '../documents/document-types'
import type { Task, TaskEvidenceItem, TaskEvidenceStage } from './task-types'

const stageLabels: Record<TaskEvidenceStage, string> = {
  before: 'Before work',
  progress: 'Work in progress',
  completion: 'Completion',
}

interface TaskEvidencePanelProps {
  task: Task
  evidence: TaskEvidenceItem[]
  canEdit: boolean
  isSaving: boolean
  onUpload: (values: { stage: TaskEvidenceStage; caption: string; file: File }) => Promise<unknown>
  onOpen: (item: TaskEvidenceItem) => Promise<void>
}

export function TaskEvidencePanel({ task, evidence, canEdit, isSaving, onUpload, onOpen }: TaskEvidencePanelProps) {
  const [isOpen, setIsOpen] = useState(evidence.length > 0)
  const [stage, setStage] = useState<TaskEvidenceStage>('progress')
  const [caption, setCaption] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const photoInput = useRef<HTMLInputElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const submit = async () => {
    if (!file) return
    await onUpload({ stage, caption: caption.trim(), file })
    setCaption('')
    setFile(null)
    if (photoInput.current) photoInput.current.value = ''
    if (fileInput.current) fileInput.current.value = ''
  }

  return (
    <details className="task-evidence" open={isOpen} onToggle={(event) => setIsOpen(event.currentTarget.open)}>
      <summary>
        Evidence for this task <span>{evidence.length}</span>
      </summary>
      <div className="task-evidence-body">
        {evidence.length > 0 ? (
          <div className="task-evidence-list">
            {evidence.map((item) => (
              <article className="task-evidence-item" key={item.id}>
                <div className="task-evidence-item-main">
                  <div>
                    <strong>{item.document_title}</strong>
                    <span className={`evidence-stage evidence-${item.stage}`}>{stageLabels[item.stage]}</span>
                  </div>
                  <small>
                    {item.caption || item.file_name} · {new Date(item.created_at).toLocaleString()}
                  </small>
                </div>
                <button className="secondary-button" type="button" onClick={() => void onOpen(item)}>Open</button>
              </article>
            ))}
          </div>
        ) : <p className="task-evidence-empty">No evidence has been attached to this task yet.</p>}

        {canEdit && (
          <div className="task-evidence-form">
            <label>
              Stage
              <select value={stage} onChange={(event) => setStage(event.target.value as TaskEvidenceStage)}>
                <option value="before">Before work</option>
                <option value="progress">Work in progress</option>
                <option value="completion">Completion</option>
              </select>
            </label>
            <label>
              Note
              <input
                maxLength={500}
                placeholder="Describe what this evidence shows"
                value={caption}
                onChange={(event) => setCaption(event.target.value)}
              />
            </label>
            <div className="evidence-file-actions">
              <input
                aria-label={`Take a photo for ${task.title}`}
                className="visually-hidden"
                ref={photoInput}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              <input
                aria-label={`Choose evidence for ${task.title}`}
                className="visually-hidden"
                ref={fileInput}
                type="file"
                accept={acceptedFileTypes.join(',')}
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              <button className="secondary-button" type="button" onClick={() => photoInput.current?.click()}>Take photo</button>
              <button className="secondary-button" type="button" onClick={() => fileInput.current?.click()}>Choose file</button>
              <button type="button" disabled={!file || isSaving} onClick={() => void submit()}>
                {isSaving ? 'Uploading…' : 'Upload evidence'}
              </button>
            </div>
            {file && <p className="selected-file">Selected: {file.name}</p>}
            <p className="task-evidence-note">Completion evidence is linked automatically as the task’s closure evidence.</p>
          </div>
        )}
      </div>
    </details>
  )
}
